import React, { useState, useEffect } from 'react'
import { Modal, Button, FormGroup, FormControl } from 'react-bootstrap'
import { useForm, Controller, SubmitHandler } from 'react-hook-form'
import ReactSelect from 'react-select'

import Label from 'live/components/forms/ui/label'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { AbsoluteLoading, RoundedModal } from '../../../../../generic'
import { createBowtie, listDiagrams, listAssets, listAllBowtiesMethods } from '../../../../../services'
import { IBowtie, IDiagram, IOperation, IAsset } from '../../../../../types'
import { AddBowtieModalBody } from './styles'

type IProps = {
    show: boolean
    handleClose: () => void
    handleSave: (id: number) => void
    item?: IBowtie /* item will be the same listItem change it when back ready */
    operations: IOperation[]
}

interface ISelect {
    value: string
    label: string
}

interface IFormData {
    asset: { value: number; label: string }
    operation: { value: number; label: string }
    riskDiagram: { value: number; label: string }
    bowtieCode: string
    methodType: ISelect
}

const formDefault: IFormData = {
    asset: null,
    operation: null,
    riskDiagram: null,
    bowtieCode: null,
    methodType: { label: null, value: null }
}

export const AddBowtieModal: React.FC<IProps> = ({ handleClose, handleSave, show, item, operations = [] }) => {
    const { handleSubmit, control, formState, setValue, watch } = useForm<IFormData>({
        mode: 'all',
        defaultValues: formDefault
    })
    const operation = watch('operation')
    const riskDiagram = watch('riskDiagram')

    const [diagramsList, setDiagramsList] = useState<IDiagram[]>([])
    const [methodsList, setMethodsList] = useState<string[]>([])
    const [assetList, setAssetList] = useState<IAsset[]>([])
    const [inLoadingCreate, setInLoadingCreate] = useState(false)
    const [inLoading, setInLoading] = useState(false)
    const [manualValidation, setManualValidation] = useState(false)

    const handleCloseProtocol = (): void => {
        setValue('asset', null)
        setValue('operation', null)
        setValue('riskDiagram', null)
        setValue('bowtieCode', null)
        setInLoadingCreate(false)
        setManualValidation(false)
        handleClose()
    }

    const handleCreate: SubmitHandler<IFormData> = async (data): Promise<void> => {
        try {
            const updateMethodType: { value: string; label: string; id?: number } = data?.methodType

            const response = await createBowtie({
                bowtieTemplateId: data.riskDiagram.value,
                asset: { id: data.asset.value },
                bowtieCode: data.bowtieCode,
                methodType: updateMethodType.value
            })
            handleSave(response.id)
            handleCloseProtocol()
        } catch (err) {
            if (err && err.message) {
                if (err.message.error) {
                    globalNotification.error(err.message.error)
                } else {
                    globalNotification.error(err.message)
                }
            }
        } finally {
            setInLoadingCreate(false)
        }
    }

    useEffect(() => {
        if (show) {
            listAssets().then(resp => setAssetList(resp.data))
            listAllBowtiesMethods().then(methods => setMethodsList(methods))

            setValue('asset', item ? { value: item.asset.id, label: item.asset.id.toString() } : null)
            setValue(
                'operation',
                item
                    ? { value: item.topEvent.hazard.operation.id, label: item.topEvent.hazard.operation.description }
                    : null
            )
            setManualValidation(true)
        }
    }, [show]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (operation) {
            setInLoading(true)
            listDiagrams({
                orderBy: 'id',
                direction: 'ASC',
                operation: operation?.value ?? undefined
            })
                .then(data => setDiagramsList(data.data))
                .finally(() => setInLoading(false))
        }
    }, [operation])

    useEffect(() => {
        if (riskDiagram) {
            setManualValidation(false)
        }
    }, [riskDiagram])

    const formatMethod = str => {
        return str
            .toLowerCase()
            .replace(/(?:^|\s)(?!da|de|do)\S/g, l => l.toUpperCase())
            .replace('_', ' ')
    }

    return (
        <RoundedModal show={show} onHide={handleCloseProtocol} aria-labelledby="contained-modal-title">
            <form onSubmit={handleSubmit(handleCreate)}>
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title">{i18n('New Bowtie')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <AddBowtieModalBody>
                        <Controller
                            rules={{ required: true }}
                            name="asset"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormGroup validationState={fieldState.error ? 'error' : 'success'}>
                                    <Label required>{i18n('Resource')}</Label>
                                    <ReactSelect
                                        options={
                                            assetList?.map(asset => ({
                                                value: asset?.id ?? 0,
                                                label: asset?.name ?? ''
                                            })) ?? []
                                        }
                                        id="bowtieList-createModal-field-asset"
                                        onChange={field.onChange}
                                        value={field.value}
                                        searchable={true}
                                        onBlur={field.onBlur}
                                    />
                                </FormGroup>
                            )}
                        />
                        <Controller
                            rules={{ required: true }}
                            name="operation"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormGroup validationState={fieldState.error ? 'error' : 'success'}>
                                    <Label required>{i18n('Operation')}</Label>
                                    <ReactSelect
                                        options={operations?.map(option => ({
                                            value: option.id,
                                            label: i18n(option.description)
                                        }))}
                                        id="bowtieList-createModal-field-operation"
                                        onChange={field.onChange}
                                        value={field.value}
                                        searchable={true}
                                        onBlur={field.onBlur}
                                    />
                                </FormGroup>
                            )}
                        />
                        <Controller
                            rules={{ required: true }}
                            name="riskDiagram"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormGroup validationState={fieldState.error ? 'error' : 'success'}>
                                    <Label required>{i18n('Risk Diagram')}</Label>
                                    <ReactSelect
                                        disabled={!operation}
                                        options={diagramsList?.map(diagram => ({
                                            value: diagram.id,
                                            label: `${diagram.hazard.name} | ${diagram.topEvent.name}`
                                        }))}
                                        id="bowtieList-createModal-field-diagram"
                                        onChange={field.onChange}
                                        value={field.value}
                                        searchable={true}
                                        onBlur={field.onBlur}
                                    />
                                </FormGroup>
                            )}
                        />
                        <Controller
                            rules={{
                                required: true,
                                validate: value => value !== item?.bowtieCode
                            }}
                            name="bowtieCode"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormGroup validationState={fieldState.error ? 'error' : 'success'}>
                                    <Label required className="control-label">
                                        {i18n('Bowtie Code')}
                                    </Label>
                                    <FormControl
                                        type="text"
                                        ref={field.ref}
                                        id="bowtieList-createModal-field-bowtieCode"
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        value={field.value}
                                        maxLength={8}
                                    />
                                </FormGroup>
                            )}
                        />
                        <Controller
                            rules={{ required: true }}
                            name="methodType"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormGroup validationState={!field.value && fieldState.error ? 'error' : 'success'}>
                                    <Label required className="control-label">
                                        {i18n('Method')}
                                    </Label>
                                    <ReactSelect
                                        clearable={false}
                                        id="bowtieList-createModal-field-method"
                                        options={methodsList
                                            .sort((a, b) => a.localeCompare(b))
                                            .map(method => ({
                                                id: method,
                                                value: method,
                                                label: i18n(formatMethod(method))
                                            }))}
                                        ref={field.ref}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        value={field.value}
                                    />
                                </FormGroup>
                            )}
                        />
                    </AddBowtieModalBody>
                </Modal.Body>
                <Modal.Footer>
                    <div className="add-modal-footer-container">
                        <div className="add-modal-button-container">
                            <Button
                                className="no-outline-button"
                                bsStyle="primary"
                                type="submit"
                                disabled={manualValidation || !formState.isValid}
                                id="bowtieList-createModal-button-create"
                            >
                                {inLoadingCreate ? (
                                    <>
                                        <i className="fa fa-spinner fa-spin" /> {i18n('Creating')}
                                    </>
                                ) : (
                                    i18n('Create')
                                )}
                            </Button>
                            <Button
                                className="no-outline-button"
                                type="button"
                                id="bowtieList-createModal-button-close"
                                onClick={handleCloseProtocol}
                            >
                                {i18n('Close')}
                            </Button>
                        </div>
                        <div className="add-moddal-required-tag">{i18n('Required')} *</div>
                    </div>
                </Modal.Footer>
                <AbsoluteLoading show={inLoading || inLoadingCreate} />
            </form>
        </RoundedModal>
    )
}
